/**
 * Phase 8 Customer 360 tabs backed by the later-phase truth: crypto deposits/withdrawals (network,
 * TXID, screening, hold), buy/sell/convert orders (rate, fee, amounts, admin-only treasury note) and the
 * customer's support threads. Read-only views; every action stays on its own queue page with step-up,
 * reason and audit. Addresses are shown truncated here (full value on the operational page).
 */
import { Badge, DataTable, formatAmount, Ltr, StateView } from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { dt, type Paged } from '../../api.ts';
import {
  CRYPTO_DEPOSIT_STATUS,
  CRYPTO_WITHDRAWAL_STATUS,
  GATE_LABEL,
  type CryptoDepositRow,
  type CryptoWithdrawalRow,
} from '../../lib/crypto-model.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { SupportThreadsTable, type SupportRow } from '../support/Support.tsx';

const unit = (a: string) => (a === 'IRT' ? 'TOMAN' : a);
const amt = (v: string | null, asset: string) =>
  v === null ? '—' : <Ltr className="hx-num">{formatAmount(v, unit(asset))}</Ltr>;
const short = (v: string | null) =>
  v ? (
    <bdi dir="ltr" className="font-mono text-[11.5px]" title={v}>
      {v.length > 18 ? `${v.slice(0, 8)}…${v.slice(-6)}` : v}
    </bdi>
  ) : (
    '—'
  );

function Panel({
  title,
  action,
  children,
  ...rest
}: { title: string; action?: ReactNode; children: ReactNode } & Record<`data-${string}`, unknown>) {
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line bg-surface-1 p-4 sm:p-5"
      {...rest}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-[14px] font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function CryptoTab({ userId }: { userId: string }) {
  const dep = useRemote<Paged<CryptoDepositRow>>(`/crypto-deposits?userId=${userId}`);
  const wd = useRemote<Paged<CryptoWithdrawalRow>>(`/crypto-withdrawals?userId=${userId}`);
  return (
    <div className="flex flex-col gap-4" data-tab-crypto>
      <Panel
        title="واریز رمزارز"
        action={
          <Link
            to="/crypto-deposits"
            className="text-[12.5px] font-semibold text-fg-muted hover:underline"
          >
            صف واریز رمزارز
          </Link>
        }
      >
        <Remote r={dep}>
          {(d) => (
            <DataTable<CryptoDepositRow>
              label="واریزهای رمزارز کاربر"
              rows={d.items}
              density="compact"
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (x) => <Ltr className="font-mono text-[12px]">{x.ref}</Ltr>,
                },
                {
                  id: 'asset',
                  header: 'دارایی / شبکه',
                  cell: (x) => <Ltr>{`${x.asset} · ${x.network}`}</Ltr>,
                },
                {
                  id: 'status',
                  header: 'وضعیت',
                  cell: (x) => {
                    const s = CRYPTO_DEPOSIT_STATUS[x.status] ?? {
                      label: x.status,
                      tone: 'neutral' as const,
                    };
                    return (
                      <span className="flex flex-col items-start gap-1">
                        <Badge tone={s.tone}>{s.label}</Badge>
                        {x.lateRegistration && <Badge tone="accent">ثبت TXID پس از مهلت</Badge>}
                      </span>
                    );
                  },
                },
                {
                  id: 'amount',
                  header: 'مقدار اعتبار',
                  numeric: true,
                  cell: (x) => amt(x.finalAmount, x.asset),
                },
                { id: 'txid', header: 'TXID', cell: (x) => short(x.txid) },
                {
                  id: 'at',
                  header: 'ثبت TXID',
                  cell: (x) => (x.txidSubmittedAt ? dt(x.txidSubmittedAt) : '—'),
                },
              ]}
              empty={<StateView compact kind="empty" title="واریز رمزارزی ثبت نشده است" />}
            />
          )}
        </Remote>
      </Panel>
      <Panel
        title="برداشت رمزارز"
        action={
          <Link
            to="/crypto-withdrawals"
            className="text-[12.5px] font-semibold text-fg-muted hover:underline"
          >
            صف برداشت رمزارز
          </Link>
        }
      >
        <Remote r={wd}>
          {(d) => (
            <DataTable<CryptoWithdrawalRow>
              label="برداشت‌های رمزارز کاربر"
              rows={d.items}
              density="compact"
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (x) => <Ltr className="font-mono text-[12px]">{x.ref}</Ltr>,
                },
                {
                  id: 'asset',
                  header: 'دارایی / شبکه',
                  cell: (x) => <Ltr>{`${x.asset} · ${x.network}`}</Ltr>,
                },
                {
                  id: 'status',
                  header: 'وضعیت',
                  cell: (x) => {
                    const s = CRYPTO_WITHDRAWAL_STATUS[x.status] ?? {
                      label: x.status,
                      tone: 'neutral' as const,
                    };
                    return <Badge tone={s.tone}>{s.label}</Badge>;
                  },
                },
                {
                  id: 'screen',
                  header: 'غربالگری',
                  cell: (x) => GATE_LABEL[x.screening.state] ?? x.screening.state,
                },
                {
                  id: 'dest',
                  header: 'مقدار مقصد',
                  numeric: true,
                  cell: (x) => amt(x.destAmount, x.asset),
                },
                { id: 'fee', header: 'کارمزد', numeric: true, cell: (x) => amt(x.fee, x.asset) },
                {
                  id: 'total',
                  header: 'کل کسر',
                  numeric: true,
                  cell: (x) => amt(x.total, x.asset),
                },
                { id: 'addr', header: 'مقصد', cell: (x) => short(x.address) },
                { id: 'payout', header: 'TXID ارسال', cell: (x) => short(x.payoutTxid) },
              ]}
              empty={<StateView compact kind="empty" title="برداشت رمزارزی ثبت نشده است" />}
            />
          )}
        </Remote>
      </Panel>
    </div>
  );
}

interface Conversion {
  id: string;
  ref: string;
  route: 'BUY' | 'SELL' | 'USDT_TO_COIN' | 'COIN_TO_USDT';
  source: { asset: string; amount: string };
  dest: { asset: string; amount: string };
  baseRate: string;
  fee: { asset: string; amount: string };
  treasury: { state: 'INTERNAL_UNRECORDED' | 'BOUGHT' | 'NOT_BOUGHT'; actualPrice: string | null };
  createdAt: string;
}
const ROUTE: Record<Conversion['route'], string> = {
  BUY: 'خرید',
  SELL: 'فروش',
  USDT_TO_COIN: 'تبدیل تتر به رمزارز',
  COIN_TO_USDT: 'تبدیل رمزارز به تتر',
};
const TREASURY: Record<Conversion['treasury']['state'], string> = {
  INTERNAL_UNRECORDED: 'بدون یادداشت خزانه',
  BOUGHT: 'خریداری‌شده توسط خزانه',
  NOT_BOUGHT: 'خریداری نشده',
};

export function TradesTab({ userId }: { userId: string }) {
  const r = useRemote<Paged<Conversion>>(`/conversions?userId=${userId}`);
  return (
    <Panel
      title="خرید، فروش و تبدیل"
      action={
        <Link
          to="/conversions"
          className="text-[12.5px] font-semibold text-fg-muted hover:underline"
        >
          همه‌ی تبدیل‌ها
        </Link>
      }
      data-tab-trades
    >
      <Remote r={r}>
        {(d) => (
          <DataTable<Conversion>
            label="سفارش‌های کاربر"
            rows={d.items}
            density="compact"
            columns={[
              {
                id: 'ref',
                header: 'کد',
                isRowHeader: true,
                cell: (c) => <Ltr className="font-mono text-[12px]">{c.ref}</Ltr>,
              },
              { id: 'route', header: 'نوع', cell: (c) => ROUTE[c.route] },
              {
                id: 'src',
                header: 'پرداختی',
                numeric: true,
                cell: (c) => amt(c.source.amount, c.source.asset),
              },
              {
                id: 'dst',
                header: 'دریافتی',
                numeric: true,
                cell: (c) => amt(c.dest.amount, c.dest.asset),
              },
              {
                id: 'rate',
                header: 'نرخ پایه',
                numeric: true,
                cell: (c) => <Ltr className="hx-num">{c.baseRate}</Ltr>,
              },
              {
                id: 'fee',
                header: 'کارمزد',
                numeric: true,
                cell: (c) => amt(c.fee.amount, c.fee.asset),
              },
              {
                id: 'treasury',
                header: 'خزانه (فقط مدیر)',
                cell: (c) => TREASURY[c.treasury.state],
              },
              { id: 'at', header: 'زمان', cell: (c) => dt(c.createdAt) },
            ]}
            empty={
              <StateView compact kind="empty" title="سفارش خرید، فروش یا تبدیلی ثبت نشده است" />
            }
          />
        )}
      </Remote>
    </Panel>
  );
}

export function SupportTab({ userId }: { userId: string }) {
  const r = useRemote<{ items: SupportRow[] }>(`/support?view=ALL&userId=${userId}`);
  return (
    <Panel
      title="پرونده‌های پشتیبانی"
      action={
        <Link to="/support" className="text-[12.5px] font-semibold text-fg-muted hover:underline">
          صندوق پشتیبانی
        </Link>
      }
      data-tab-support
    >
      <Remote r={r}>
        {(d) => <SupportThreadsTable rows={d.items} label="پرونده‌های پشتیبانی کاربر" />}
      </Remote>
    </Panel>
  );
}
